import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { useFetchProduct, useSimilarProductsRow } from '@jane/dm/internal';
import { ProductDetailSkeleton } from '@jane/kiosk/components/lib/ProductDetailSkeleton/ProductDetailSkeleton';
import type { RootLoaderType } from '@jane/kiosk/data-access/lib/loaders/rootLoader';
import type { InternalFeeling } from '@jane/kiosk/data-access/lib/queries/reviews';
import { useProductFeelings } from '@jane/kiosk/data-access/lib/queries/reviews';
import { useTypedRouteLoaderData } from '@jane/kiosk/hooks/lib/useTypedRouteLoaderData';
import { useEnsureProductId } from '@jane/shared-ecomm/hooks';

interface ProductDetailsContext {
  feelings: InternalFeeling[];
  isFeelingsLoading: boolean;
  isPdpRowLoading: boolean;
  pdpRowInstance: SmartSort<MaterializedMenuProduct> | undefined;
  product: MaterializedMenuProduct;
  productId: number;
  storeId: number;
}

const ProductDetailsContext = createContext<ProductDetailsContext | null>(null);
ProductDetailsContext.displayName = 'ProductDetails';

export const ProductDetailsProvider = ({ children }: PropsWithChildren) => {
  const { storeId, store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { productId: productIdParam } = useParams<{ productId: string }>();

  const productId = useEnsureProductId(productIdParam);

  const {
    product: smartSortProduct,
    isLoading: isProductLoading,
    isSuccess: isProductSuccess,
  } = useFetchProduct<MaterializedMenuProduct>({
    productId,
    storeId,
  });

  const product = smartSortProduct?.attributes;

  if (isProductSuccess && !product) {
    throw new Error(
      '[ProductDetailsProvider]: No product returned — it is likely out of stock or hidden by the store.'
    );
  }

  const { data: feelings = [], isLoading: isFeelingsLoading } =
    useProductFeelings(productId, {
      enabled: store.product_reviews_enabled,
    });

  const { instance: pdpRowInstance, isLoading: isPdpRowLoading } =
    useSimilarProductsRow<MaterializedMenuProduct>({
      productId,
      searchAttributes: ['*'],
      storeId,
    });

  const contextValue = useMemo<ProductDetailsContext | null>(
    () =>
      product
        ? {
            feelings,
            isFeelingsLoading,
            isPdpRowLoading,
            pdpRowInstance,
            product,
            productId,
            storeId,
          }
        : null,
    [
      feelings,
      isFeelingsLoading,
      isPdpRowLoading,
      pdpRowInstance,
      product,
      productId,
      storeId,
    ]
  );

  if (isProductLoading || !contextValue) {
    return <ProductDetailSkeleton />;
  }

  return (
    <ProductDetailsContext.Provider value={contextValue}>
      {children}
    </ProductDetailsContext.Provider>
  );
};

export const useProductDetails = () =>
  useContextEnsuringProvider(ProductDetailsContext);
